"use client";

import { motion } from "framer-motion";
import {
  Clock,
  Shield,
  CheckCircle,
  Smartphone,
  Globe,
  ArrowRight,
  WalletCards,
  Sparkles,
} from "lucide-react";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";

export default function CardSoon() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Mobile & Contactless",
      description:
        "Tap to pay with Apple Pay, Google Pay & contactless terminals",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Acceptance",
      description: "Works worldwide wherever Mastercard is accepted",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Advanced Security",
      description: "Freeze card, real-time alerts, virtual cards & encryption",
    },
    {
      icon: <WalletCards className="w-6 h-6" />,
      title: "Physical + Virtual",
      description: "One physical card + multiple virtual cards for online use",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10 lg:px-12">
      {/* Background subtle gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 sm:-top-40 sm:-right-40 w-64 h-64 sm:w-96 sm:h-96 bg-emerald-600/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 sm:-bottom-40 sm:-left-40 w-64 h-64 sm:w-96 sm:h-96 bg-teal-600/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto space-y-10 sm:space-y-12 md:space-y-16">
        {/* Header */}
        <div className="text-center space-y-4 sm:space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 sm:px-5 sm:py-2 bg-emerald-950/40 border border-emerald-800/50 rounded-full text-emerald-300 text-xs sm:text-sm font-medium backdrop-blur-sm">
            <Clock className="w-4 h-4" />
            <span>Coming Soon</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight bg-gradient-to-r from-white via-emerald-300 to-teal-300 bg-clip-text text-transparent">
            CashTap Card
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-xl mx-auto leading-relaxed px-2 sm:px-0">
            Turn your wallet balance into everyday spending power with a sleek
            <br className="hidden sm:block" />
            Mastercard debit card — physical + virtual.
          </p>
        </div>

        {/* Card Preview */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative mx-auto w-full max-w-[340px] xs:max-w-[380px] sm:max-w-sm md:max-w-md lg:max-w-lg"
        >
          <div className="relative aspect-[1.58/1] rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700/70 bg-gradient-to-br from-gray-900 via-black to-gray-950 shadow-2xl shadow-black/60">
            {/* Shine / gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/8 via-transparent to-transparent opacity-60" />

            {/* Chip */}
            <div className="absolute left-4 top-4 xs:left-5 xs:top-5 sm:left-6 sm:top-6 md:left-8 md:top-8">
              <div className="w-10 h-8 xs:w-11 xs:h-8.5 sm:w-12 sm:h-9 md:w-14 md:h-10 bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 rounded-lg shadow-xl flex items-center justify-center">
                <div className="grid grid-cols-4 gap-0.5">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-1 h-1.5 xs:w-1.5 xs:h-2 bg-amber-800/60 rounded-full"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* NFC / Sparkles */}
            <div className="absolute right-4 top-4 xs:right-5 xs:top-5 sm:right-6 sm:top-6 md:right-8 md:top-8">
              <div className="p-1.5 xs:p-2 bg-white/10 backdrop-blur-md rounded-lg border border-white/20">
                <Sparkles className="w-5 h-5 xs:w-6 xs:h-6 text-emerald-400" />
              </div>
            </div>

            {/* Logo & branding */}
            <div className="absolute bottom-4 left-4 right-4 xs:bottom-5 xs:left-5 xs:right-5 sm:bottom-6 sm:left-6 sm:right-6 md:bottom-8 md:left-8 md:right-8 flex items-center justify-between">
              <div className="flex items-center gap-2 xs:gap-3">
                <div className="w-9 h-9 xs:w-10 xs:h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                  <span className="text-xl xs:text-2xl md:text-3xl font-black text-emerald-950">
                    $
                  </span>
                </div>
                <div>
                  <p className="text-base xs:text-lg sm:text-xl font-bold text-white tracking-wide">
                    Cash<span className="text-emerald-400">Tap</span>
                  </p>
                  <p className="text-xs xs:text-sm text-gray-300">
                    Debit Mastercard
                  </p>
                </div>
              </div>

              <div className="flex -space-x-2">
                <div className="w-7 h-7 xs:w-8 xs:h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-500 border-2 border-white/40" />
                <div className="w-7 h-7 xs:w-8 xs:h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-400 border-2 border-white/40" />
              </div>
            </div>

            {/* Coming Soon overlay */}
            <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex items-center justify-center px-4">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-3 xs:mb-4">
                  <Clock className="w-4 h-4 xs:w-5 xs:h-5 text-emerald-300" />
                  <span className="font-semibold text-white text-sm xs:text-base">
                    Coming Soon
                  </span>
                </div>
                <p className="text-lg xs:text-xl sm:text-2xl font-bold text-white mb-1 xs:mb-2">
                  We're getting it ready for you
                </p>
                <p className="text-gray-300 text-sm xs:text-base">
                  Expected Q2 2026
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
              className="bg-gradient-to-br from-gray-900/60 to-black/40 border border-gray-800/60 rounded-2xl p-5 sm:p-6 hover:border-emerald-600/40 hover:shadow-emerald-900/20 transition-all group"
            >
              <div className="w-11 h-11 xs:w-12 xs:h-12 rounded-xl bg-emerald-950/50 border border-emerald-800/40 flex items-center justify-center text-emerald-400 mb-3 xs:mb-4 group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>
              <h4 className="text-base xs:text-lg font-semibold text-white mb-1.5 xs:mb-2">
                {feature.title}
              </h4>
              <p className="text-gray-400 text-xs xs:text-sm leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* What to expect */}
        <div className="bg-gradient-to-br from-gray-900/50 to-black/40 border border-gray-800/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2 bg-emerald-950/40 border border-emerald-800/50 rounded-full text-emerald-300 text-sm sm:font-medium">
              <Shield className="w-5 h-5" />
              <span>Security & Control First</span>
            </div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
              What you'll get at launch
            </h3>

            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 text-left text-gray-300 pt-2 sm:pt-4">
              {[
                {
                  text: "Instant freeze / unfreeze",
                  sub: "One tap in the app",
                },
                {
                  text: "Real-time spending alerts",
                  sub: "Never miss a transaction",
                },
                {
                  text: "Virtual cards for safety",
                  sub: "One-time or merchant locked",
                },
                {
                  text: "No foreign transaction fees",
                  sub: "Use it anywhere globally",
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 sm:gap-4">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white text-sm sm:text-base">
                      {item.text}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-400">
                      {item.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-6 sm:pt-8">
              <button
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-xl sm:rounded-2xl shadow-lg shadow-emerald-900/30 transition-all hover:shadow-xl hover:shadow-emerald-700/40 active:scale-[0.98] text-sm sm:text-base"
              >
                Back to Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Loading teaser */}
        <div className="text-center pt-8 pb-10 sm:pb-16">
          <GridLoader color="#61CE70" size={10} margin={6} />
          <p className="text-gray-400 mt-5 sm:mt-6 text-sm sm:text-base">
            Preparing something powerful for you...
          </p>
        </div>
      </div>
    </div>
  );
}
